import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import classes from '@exp/utilities/buildClassName';

import { Layout } from '../constants/constants';
import { ModeConfig } from '../constants/modes';
import MasterFlow from '../pages/MasterFlow';
import { selectConfig, selectIsBackable } from '../redux/context';
import { selectHistory } from '../redux/history';
import { useAppSelector } from '../redux/hooks';
import { PageChangeEvent, PageComponent } from '../types/BaseFlow';
import { TemplateMap, Theme } from './types';
import ModalLayoutV1 from './ModalLayoutV1';
import ModalLayoutV2 from './ModalLayoutV2';
import ModalLayoutV3 from './ModalLayoutV3';
import StaticLayout from './StaticLayout';
import StaticLayoutV3 from './StaticLayoutV3';
import AccordionLayout from './AccordionLayout';

const Templates: TemplateMap = {
  interests: {
    v1: AccordionLayout,
    v2: AccordionLayout,
    v3: AccordionLayout,
  },
  manage: {
    v1: StaticLayoutV3,
    v2: StaticLayoutV3,
    v3: StaticLayoutV3,
    vets: StaticLayoutV3,
  },
  modal: {
    v1: ModalLayoutV1,
    v2: ModalLayoutV2,
    v3: ModalLayoutV3,
    vets: ModalLayoutV3,
  },
  qualify: {
    v1: StaticLayoutV3,
    v2: StaticLayoutV3,
    v3: StaticLayoutV3,
    vets: StaticLayoutV3,
  },
  setup: {
    v1: AccordionLayout,
    v2: AccordionLayout,
    v3: AccordionLayout,
    vets: AccordionLayout,
  },
  static: {
    v1: StaticLayout,
    v2: StaticLayout,
    v3: StaticLayout,
    vets: ModalLayoutV3,
  },
};

const MODAL_OPEN_CLASS = 'exp-modal-open';
const TALL_PAGE_CLASS = 'exp-modal-tall-page';
const TALL_PAGE_CLASS_V2 = 'exp-modal-tall-page-v2';
const TAKEOVER_PAGE_CLASS = 'exp-modal-takeover-open';
const TAKEOVERVETS_PAGE_CLASS = 'exp-modal-vets-open';

const SETUP_LAYOUTS = [Layout.SETUP, Layout.QUALIFY, Layout.MANAGE, Layout.INTEREST];

type ContainerProps = {
  close: (options?: { url?: string }) => void;
  config: ModeConfig;
  layout?: Layout;
  onPageChange?: (page: PageChangeEvent) => void;
  pages: PageComponent[];
  parent?: string;
  theme?: Theme;
};

/**
 * Onboarding Container Component
 */
const Container = ({
  close,
  config,
  layout = Layout.MODAL,
  onPageChange,
  pages,
  parent,
  theme = 'v1',
}: ContainerProps) => {
  const isBackable = useAppSelector(selectIsBackable);
  const { toggles }: ModeConfig = useAppSelector(selectConfig);
  const [current, previous] = useAppSelector(selectHistory);
  const containerRef = useRef(null);
  const currentPageRef = useRef<PageChangeEvent | null>(null);

  useEffect(() => {
    if (layout === Layout.MODAL && !toggles.preventClose) {
      const listener = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          close();
        }
      };

      document.addEventListener('keydown', listener);
      return () => {
        document.removeEventListener('keydown', listener);
      };
    }

    return undefined;
  }, [close, layout, toggles]);

  useEffect(() => {
    // Only lock body scroll for the actual modal layout — full-page layouts (qualify,
    // setup, static) need the body to remain scrollable.
    if (layout !== Layout.MODAL) return undefined;

    const parentElem = document.querySelector(parent || 'body');
    if (parentElem) {
      parentElem.classList.add(MODAL_OPEN_CLASS);
    }

    return () => {
      const el = document.querySelector(parent || 'body');
      if (el) {
        el.classList.remove(MODAL_OPEN_CLASS, TALL_PAGE_CLASS, TALL_PAGE_CLASS_V2);
      }
    };
  }, [parent, layout]);

  useEffect(() => {
    const modalCloseListener = () => {
      close();
    };
    const parentElem = document.querySelector(parent || 'body');

    if (parentElem && ['v3', 'vets'].includes(theme)) {
      parentElem.classList.add(TAKEOVER_PAGE_CLASS);
      if (theme === 'vets') {
        parentElem.classList.add(TAKEOVERVETS_PAGE_CLASS);
      }
      // Only a real dismissible modal takeover should close the whole widget on browser
      // back. Layout.MANAGE/QUALIFY (e.g. manageTeams) reuse the v3/vets theme purely for
      // styling on a permanent static page, so they must not arm this listener — otherwise
      // any in-page pushState (e.g. GroupSearchQualifyPage's Team Finder chat history entry)
      // gets misread as "the user backed out of the takeover" and unmounts the whole widget.
      if (layout === Layout.MODAL) {
        window.addEventListener('popstate', modalCloseListener);
      }
    }

    return () => {
      const el = document.querySelector(parent || 'body');
      if (el) {
        el.classList.remove(TAKEOVER_PAGE_CLASS);
      }
      window.removeEventListener('popstate', modalCloseListener);
    };
  }, [close, layout, parent, theme]);

  const finish = useCallback(
    (url?: string) => {
      close({ url });
    },
    [close],
  );

  const handlePageChange = useCallback(
    (page: PageChangeEvent) => {
      // Scroll the page to the top on each render, for easier viewing
      if ([Layout.MODAL, Layout.QUALIFY].includes(layout)) {
        const parentElem = document.querySelector(parent || 'body');
        if (parentElem) {
          // Toggle the tall page class
          // TODO maybe just detect if the page is overflowing and auto-manage the tall page-ness?
          parentElem.classList.toggle(theme === 'v2' ? TALL_PAGE_CLASS_V2 : TALL_PAGE_CLASS, page.tallPage);
        }

        if (!config.toggles?.avoidScrolling) {
          if (!currentPageRef.current?.tallPage && page.tallPage) {
            // The tall page transition can mess with the scroll into view. Scroll to top.
            window.scrollTo({ behavior: 'smooth', top: 0 });
          } else if (containerRef.current) {
            // Ensure the container is visible
            const rect = containerRef.current.getBoundingClientRect();
            if (rect.top < 0 || rect.bottom < 0) {
              containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }
      }

      currentPageRef.current = page;
      onPageChange?.(page);
    },
    [layout, config.toggles?.avoidScrolling, onPageChange, parent, theme],
  );

  const onPrevious = useCallback(() => {
    // Delegate the previous to the parent of the current page
    currentPageRef.current?.parent?.previous?.();
  }, []);

  const onSkip = useCallback(() => {
    // Delegate the skip to the parent of the current page
    currentPageRef.current?.parent?.skip?.();
  }, []);

  const goTo = useCallback((page: number) => {
    currentPageRef.current?.parent?.goTo?.(page);
  }, []);

  const backable = useMemo(() => {
    // The page is backable if it says so - or - the previous page is replayable
    if (isBackable !== undefined || current?.backable !== undefined) {
      return isBackable || current.backable;
    }
    return previous?.replayable || false;
  }, [current, isBackable, previous]);

  // Use the template for the configured layout
  const Template = Templates[layout]?.[theme] || ModalLayoutV1;

  return (
    <div
      className={classes(
        'exp-onboarding',
        `exp-onb-mode-${layout}`,
        `exp-onb-theme-${theme}`,
        `exp-onboarding-${layout === Layout.STATIC ? 'static' : 'modal'}`,
        current?.className && `${current.className}`,
        current?.parent?.className && `${current.parent.className}`,
        backable && 'exp-onb-backable',
        current?.closable && 'exp-onb-closable',
        current?.skippable && 'exp-onb-skippable',
      )}
      data-layout={layout}
      data-prev-flow-class={previous?.parent?.className}
      data-prev-page-class={previous?.className}
      data-theme={theme}
      id={`exp-onboarding-${layout === Layout.MODAL ? 'modal' : SETUP_LAYOUTS.includes(layout) ? 'setup' : 'static'}`}
    >
      <Template
        currentPageName={current?.name}
        goTo={goTo}
        onClose={current?.closable ? close : undefined}
        onPrevious={backable ? onPrevious : undefined}
        onSkip={current?.skippable ? onSkip : undefined}
        parent={current?.parent}
        ref={containerRef}
      >
        <MasterFlow finish={finish} onPageChange={handlePageChange} pages={pages} />
      </Template>
    </div>
  );
};

Container.displayName = 'Container';

export default Container;
